/**
 * React Query hooks for CV Note operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import noteService from '../services/note.service';

interface UseCVNotesQueryParams {
  cvId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useCVNotesQuery = ({
  cvId,
  workspaceId,
  idToken,
  enabled = true,
}: UseCVNotesQueryParams) => {
  return useQuery({
    queryKey: ['cv-notes', cvId],
    queryFn: async () => {
      if (!cvId || !workspaceId || !idToken) {
        throw new Error('CV ID, Workspace ID, and token are required');
      }

      return await noteService.listNotes(cvId, workspaceId, idToken);
    },
    enabled: enabled && !!cvId && !!workspaceId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseCreateNoteMutationParams {
  cvId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
}

export const useCreateNoteMutation = ({
  cvId,
  workspaceId,
  idToken,
}: UseCreateNoteMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      if (!cvId || !workspaceId || !idToken) {
        throw new Error('CV ID, Workspace ID, and token are required');
      }

      return await noteService.createNote(cvId, content, workspaceId, idToken);
    },
    onSuccess: () => {
      // Invalidate notes query to refetch
      queryClient.invalidateQueries({ queryKey: ['cv-notes', cvId] });
    },
  });
};

interface UseDeleteNoteMutationParams {
  cvId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
}

export const useDeleteNoteMutation = ({
  cvId,
  workspaceId,
  idToken,
}: UseDeleteNoteMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      if (!cvId || !workspaceId || !idToken) {
        throw new Error('CV ID, Workspace ID, and token are required');
      }

      return await noteService.deleteNote(cvId, noteId, workspaceId, idToken);
    },
    onSuccess: () => {
      // Invalidate notes query to refetch
      queryClient.invalidateQueries({ queryKey: ['cv-notes', cvId] });
    },
  });
};
